import { useState } from 'react'
import { isAxiosError } from 'axios'
import { Search, Wallet } from 'lucide-react'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { bucketLabel, date, dateTime, dpd, inrFull } from '@/lib/format'

import {
  lmsErrorDetail,
  useMockLenderPayment,
  useMockLmsLoan,
  type MockLmsLoan,
} from '@/features/lms/api'

/**
 * Demo controls for the mock LMS: look a loan up, and record a payment the
 * customer made at the lender's branch — outside the platform — so the
 * next LMS read closes the case.
 *
 * Renders inside SettingsPage's form, like PaymentGatewayCredentialsCard, so
 * it has no form element of its own and Enter is caught on the inputs —
 * otherwise it would submit the settings form around it.
 */
export function MockLmsCard() {
  const lookup = useMockLmsLoan()
  const pay = useMockLenderPayment()

  const [loanNo, setLoanNo] = useState('')
  const [amount, setAmount] = useState('')
  const [shown, setShown] = useState<{ loanNo: string; loan: MockLmsLoan; paid: boolean } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const trimmed = loanNo.trim()
  const amountValue = amount.trim() === '' ? undefined : Number(amount)
  const amountValid = amountValue === undefined || (Number.isFinite(amountValue) && amountValue > 0)
  const busy = lookup.isPending || pay.isPending

  function describe(err: unknown, no: string): string {
    const detail = lmsErrorDetail(err)
    const status = isAxiosError(err) ? err.response?.status : undefined
    if (status === 404 && detail.includes('not_in_lms')) return `Loan ${no} isn't in the mock LMS.`
    if (status === 409 && detail.includes('lms_not_mock')) return 'The saved LMS mode is not Mock — save it first.'
    return detail || 'The mock LMS call failed.'
  }

  function onLookup() {
    if (!trimmed || busy) return
    setError(null)
    lookup.mutate(trimmed, {
      onSuccess: (loan) => setShown({ loanNo: trimmed, loan, paid: false }),
      onError: (err) => { setShown(null); setError(describe(err, trimmed)) },
    })
  }

  function onPay() {
    if (!trimmed || !amountValid || busy) return
    setError(null)
    pay.mutate({ loanNo: trimmed, amount: amountValue }, {
      onSuccess: (loan) => { setShown({ loanNo: trimmed, loan, paid: true }); setAmount('') },
      onError: (err) => setError(describe(err, trimmed)),
    })
  }

  const l = shown?.loan
  const emiDue = l ? Number(l.overduePrincipal) + Number(l.overdueInterest) : 0

  return (
    <Card>
      <CardHeader>
        <CardTitle>Mock LMS (demo)</CardTitle>
        <CardDescription>
          Look up a loan in the mock LMS, or record a payment the customer made
          at the lender's branch. Saved separately from the settings above.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-[1fr_auto] items-end">
          <div className="space-y-1.5">
            <Label htmlFor="mock-lms-loan">Loan number</Label>
            <Input
              id="mock-lms-loan"
              value={loanNo}
              onChange={(e) => setLoanNo(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); onLookup() }
              }}
            />
          </div>
          <Button type="button" variant="outline" disabled={!trimmed || busy} onClick={onLookup}>
            <Search className="h-3.5 w-3.5" />
            {lookup.isPending ? 'Looking up…' : 'Look up'}
          </Button>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {shown && l && (
          <div className="rounded-md border border-slate-200 bg-navy-50/40 p-3">
            <p className="mb-2 text-2xs uppercase tracking-wider text-muted-foreground">
              {shown.loanNo} · as of {dateTime(l.asOfAt)}
            </p>
            <dl className="grid grid-cols-[10rem_1fr] gap-x-4 gap-y-1.5 text-sm">
              <dt className="text-2xs uppercase tracking-wider text-muted-foreground">DPD</dt>
              <dd className="tabular-nums">{dpd(l.dpd)}</dd>
              <dt className="text-2xs uppercase tracking-wider text-muted-foreground">Bucket</dt>
              <dd>{bucketLabel(l.bucket)}</dd>
              <dt className="text-2xs uppercase tracking-wider text-muted-foreground">EMI due</dt>
              <dd className="tabular-nums font-medium text-navy-900">{inrFull(emiDue)}</dd>
              <dt className="text-2xs uppercase tracking-wider text-muted-foreground">Late payment charges</dt>
              <dd className="tabular-nums">{inrFull(Number(l.lppCharges))}</dd>
              <dt className="text-2xs uppercase tracking-wider text-muted-foreground">Bounce charges</dt>
              <dd className="tabular-nums">{inrFull(Number(l.bounceCharges))}</dd>
              <dt className="text-2xs uppercase tracking-wider text-muted-foreground">Last payment</dt>
              <dd className="tabular-nums">
                {l.lastPaymentDate
                  ? `${date(l.lastPaymentDate)} · ${inrFull(l.lastPaymentAmount == null ? null : Number(l.lastPaymentAmount))}`
                  : '—'}
              </dd>
            </dl>
            {shown.paid && (
              <p className="mt-3 text-xs text-navy-800">
                Open the case, or run Refresh from LMS now, to see it close.
              </p>
            )}
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-[1fr_auto] items-end">
          <div className="space-y-1.5">
            <Label htmlFor="mock-lms-amount">Amount (optional)</Label>
            <Input
              id="mock-lms-amount"
              type="number"
              min={0}
              step="0.01"
              placeholder="Blank = everything due"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.preventDefault()
              }}
            />
            {!amountValid && (
              <p className="text-2xs text-negative">Enter an amount above zero, or leave it blank.</p>
            )}
          </div>
          <Button type="button" disabled={!trimmed || !amountValid || busy} onClick={onPay}>
            <Wallet className="h-3.5 w-3.5" />
            {pay.isPending ? 'Recording…' : 'Record payment at the lender'}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
